import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Activity } from 'lucide-react';
import { Button, Card, Chip, FormField, Input, Select, Skeleton, StatTile } from '@/ui';
import {
  METRIC_PERIODS,
  TIMESERIES_METRICS,
  fetchActivity,
  fetchOverviewMetrics,
  fetchTimeseries,
  fetchUserMetrics,
  seriesCurrencies,
  type MetricPeriod,
  type TimeseriesMetric,
} from './api';
import { adminKeys } from './query-keys';
import {
  ACTIVITY_EVENT_LABEL_KEYS,
  PERIOD_LABEL_KEYS,
  TIMESERIES_METRIC_LABEL_KEYS,
  USER_TYPE_LABEL_KEYS,
} from './labels';
import { adminErrorMessage } from './error-message';
import { formatCompact, formatCount, formatDate, formatDateTime, formatPercent } from './format';
import { SeriesChart } from './SeriesChart';
import { BroadcastComposer } from './BroadcastComposer';
import { ListStatus, LoadMore } from './ListStatus';
import { flattenPages, usePagedQuery } from './use-paged-query';
import styles from './Admin.module.css';

/**
 * PLATFORM METRIKLERI (`/yonetim/metrikler`) + DUYURULAR.
 *
 * ============== "totals" PENCEREYI YOK SAYAR — ETIKETLEMEK SART ============
 * `GET iam /admin/v1/metrics/users` iki blok doner ve BIRBIRLERINE
 * BENZEMEZLER:
 *
 *   totals -> SU ANIN goruntusu. `from`/`to` bu bloku ETKILEMEZ.
 *   series -> secilen pencereye gore gruplanmis kayitlar.
 *
 * Ikisi ayni baslik altinda yan yana cizilseydi, "son 7 gun" secili bir
 * ekranda 5 yazan bir kutu "son 7 gunde 5 kullanici" diye okunurdu — oysa
 * "sistemde toplam 5 kullanici var" demek. Bu yuzden iki blok AYRI kartlarda,
 * canli olan blokta acik bir not ve bir rozetle duruyor.
 *
 * ============================== DUYURULAR ==================================
 * Duyuru bestecisi bu sayfanin ALTINDA bir bolum (`#duyuru`). Menude ayri bir
 * oge yok; duyuru gondermeden once bakilacak sayilar zaten burada.
 */

const isPeriod = (value: string): value is MetricPeriod =>
  (METRIC_PERIODS as readonly string[]).includes(value);

const isMetric = (value: string): value is TimeseriesMetric =>
  (TIMESERIES_METRICS as readonly string[]).includes(value);

/** Bos dize -> null; `<input type="date">` bos degeri boyle bildirir. */
const orNull = (value: string): string | null => (value === '' ? null : value);

export function PlatformMetricsPage() {
  const { t } = useTranslation();

  const [userPeriod, setUserPeriod] = useState<MetricPeriod>('day');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [metric, setMetric] = useState<TimeseriesMetric>('tasks');
  const [metricPeriod, setMetricPeriod] = useState<MetricPeriod>('month');

  const userMetrics = useQuery({
    queryKey: adminKeys.metricsUsers(userPeriod, orNull(from), orNull(to)),
    queryFn: () => fetchUserMetrics({ period: userPeriod, from: orNull(from), to: orNull(to) }),
  });

  const overview = useQuery({
    queryKey: adminKeys.metricsOverview(),
    queryFn: fetchOverviewMetrics,
  });

  const timeseries = useQuery({
    queryKey: adminKeys.metricsTimeseries(metric, metricPeriod),
    queryFn: () => fetchTimeseries({ metric, period: metricPeriod }),
  });

  // HACIM SERISI BIRIM BASINA (2026-09-23, karar Q13): ayni kovada TRY ve USD icin ayri
  // noktalar geliyor. Hepsi tek grafige basilsaydi ayni etikette iki cubuk (ya da toplanmis
  // anlamsiz bir deger) olurdu. Birim secilir, varsayilan sunucunun ilk birimi.
  const volumeCurrencies =
    metric === 'volume' && timeseries.data ? seriesCurrencies(timeseries.data) : [];
  const [volumeCurrencyChoice, setVolumeCurrencyChoice] = useState<string | null>(null);
  const volumeCurrency =
    volumeCurrencyChoice !== null && volumeCurrencies.includes(volumeCurrencyChoice)
      ? volumeCurrencyChoice
      : (volumeCurrencies[0] ?? null);
  const chartPoints = (timeseries.data ?? []).filter(
    (point) => volumeCurrency === null || point.currency === null || point.currency === volumeCurrency,
  );

  const activity = usePagedQuery(adminKeys.activity(), (pageParam) => fetchActivity(pageParam));
  const activityRows = flattenPages(activity.data);

  const periodOptions = METRIC_PERIODS.map((value) => ({
    value,
    // Sabit HARITA anahtar tutar; ceviri BURADA, render sirasinda.
    label: t(PERIOD_LABEL_KEYS[value] ?? value),
  }));

  return (
    <div className={styles.page}>
      <Card padding="md">
        <div className={styles.pageHead}>
          <div>
            <h1 className={styles.title}>{t('admin.metrics.title')}</h1>
            <p className={styles.subtitle}>{t('admin.metrics.subtitle')}</p>
          </div>
          {/* Duyuru bolumu ayni sayfada; baglanti gercek bir <a> (orta tik,
              yeni sekme, adresin kopyalanmasi calisir). */}
          <a href="#duyuru" className={styles.linkButton}>
            {t('admin.metrics.jumpToBroadcast')}
          </a>
        </div>
      </Card>

      {/* ---------------- Canli kullanici goruntusu ---------------- */}
      <Card padding="md">
        <div className={styles.pageHead}>
          <h2 className={styles.sectionTitle}>{t('admin.metrics.liveTotalsTitle')}</h2>
          <Chip tone="info">{t('admin.metrics.liveBadge')}</Chip>
        </div>
        {/* ⚠ Bu not sussuz degil: asagidaki tarih araligi bu kutulari
            ETKILEMIYOR ve bunu yazmadan gostermek yanlis okunur. */}
        <p className={styles.notice}>{t('admin.metrics.liveTotalsNotice')}</p>

        {userMetrics.isPending ? <Skeleton shape="block" height="120px" /> : null}
        {userMetrics.isError ? (
          <p className={styles.error}>{adminErrorMessage(userMetrics.error, t)}</p>
        ) : null}
        {userMetrics.data ? (
          <div className={styles.tileGrid}>
            <StatTile
              label={t(USER_TYPE_LABEL_KEYS.customer ?? '')}
              value={formatCount(userMetrics.data.totals.customers)}
            />
            <StatTile
              label={t(USER_TYPE_LABEL_KEYS.exchange ?? '')}
              value={formatCount(userMetrics.data.totals.exchanges)}
            />
            <StatTile
              label={t('admin.status.active')}
              value={formatCount(userMetrics.data.totals.active)}
              hint={t('admin.metrics.inactiveHint', {
                n: userMetrics.data.totals.inactive,
              })}
            />
            <StatTile
              label={t('admin.metrics.organizations')}
              value={`${formatCount(userMetrics.data.totals.activeOrganizations)} / ${formatCount(
                userMetrics.data.totals.organizations,
              )}`}
              hint={t('admin.metrics.organizationsHint')}
            />
          </div>
        ) : null}
      </Card>

      {/* ---------------- Pencereye bagli kayit serisi ---------------- */}
      <Card padding="md">
        <h2 className={styles.sectionTitle}>{t('admin.metrics.signupsTitle')}</h2>
        <p className={styles.muted}>{t('admin.metrics.signupsNotice')}</p>

        <div className={styles.filterRow}>
          <FormField label={t('admin.metrics.periodLabel')} className={styles.filterField}>
            <Select
              value={userPeriod}
              options={periodOptions}
              onChange={(event) => {
                const value = event.target.value;
                if (isPeriod(value)) setUserPeriod(value);
              }}
            />
          </FormField>
          <FormField label={t('labels.dateFrom')} className={styles.filterField}>
            <Input
              type="date"
              value={from}
              onChange={(event) => {
                setFrom(event.target.value);
              }}
            />
          </FormField>
          <FormField label={t('labels.dateTo')} className={styles.filterField}>
            <Input
              type="date"
              value={to}
              onChange={(event) => {
                setTo(event.target.value);
              }}
            />
          </FormField>
          <div className={styles.filterActions}>
            <Button
              tone="ghost"
              onClick={() => {
                setFrom('');
                setTo('');
              }}
            >
              {t('filter.clearAll')}
            </Button>
          </div>
        </div>

        {userMetrics.data ? (
          <SeriesChart
            points={userMetrics.data.series.map((point) => ({
              label: formatDate(point.bucket),
              value: point.signups,
            }))}
            title={t('admin.metrics.signupsTitle')}
            formatValue={formatCount}
            seriesIndex={2}
            emptyLabel={t('admin.metrics.noSeries')}
          />
        ) : null}
      </Card>

      {/* ---------------- Pazaryeri ozeti ---------------- */}
      <Card padding="md">
        <h2 className={styles.sectionTitle}>{t('admin.metrics.marketTitle')}</h2>
        {overview.isPending ? <Skeleton shape="block" height="120px" /> : null}
        {overview.isError ? (
          <p className={styles.error}>{adminErrorMessage(overview.error, t)}</p>
        ) : null}
        {overview.data ? (
          <>
            <div className={styles.tileGrid}>
              <StatTile
                label={t('admin.metrics.taskTotal')}
                value={formatCount(overview.data.tasks.total)}
              />
              <StatTile
                label={t('admin.metrics.taskWon')}
                value={formatCount(overview.data.tasks.won)}
                hint={t('admin.metrics.winRate', {
                  rate: formatPercent(overview.data.tasks.winRate),
                })}
              />
              <StatTile
                label={t('admin.metrics.offerCount')}
                value={formatCount(overview.data.offers.offerCount)}
                hint={t('admin.metrics.offeredTaskCount', {
                  n: overview.data.offers.offeredTaskCount,
                })}
              />
              <StatTile
                label={t('admin.metrics.offeringOrganizations')}
                value={formatCount(overview.data.offers.offeringOrganizationCount)}
                hint={t('admin.metrics.offeringUsers', {
                  n: overview.data.offers.offeringUserCount,
                })}
              />
            </div>

            <h3 className={styles.sectionTitle}>{t('admin.metrics.volumeTitle')}</h3>
            <table className={styles.chartTable}>
              <thead>
                <tr>
                  <th scope="col">{t('admin.metrics.currency')}</th>
                  <th scope="col">{t('admin.metrics.taskCount')}</th>
                  <th scope="col">{t('admin.metrics.wonTaskCount')}</th>
                  <th scope="col">{t('admin.metrics.totalAmount')}</th>
                  <th scope="col">{t('admin.metrics.wonCounterValue')}</th>
                </tr>
              </thead>
              <tbody>
                {/* Her tutar KENDI biriminde etiketli: miktar islem biriminde, karsilik karsi
                    birimde (EUR/USD'de USD). Satirlar arasi toplam satiri BILINCLI olarak yok. */}
                {overview.data.volume.map((row) => (
                  <tr key={row.taskTypeCode}>
                    <th scope="row">{row.taskTypeCode}</th>
                    <td>{formatCount(row.taskCount)}</td>
                    <td>{formatCount(row.wonTaskCount)}</td>
                    <td>{withUnit(formatCompact(row.totalAmount), row.currency)}</td>
                    <td>{withUnit(formatCompact(row.wonCounterValue), row.counterCurrency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : null}
      </Card>

      {/* ---------------- Zaman serisi ---------------- */}
      <Card padding="md">
        <h2 className={styles.sectionTitle}>{t('admin.metrics.timeseriesTitle')}</h2>
        <div className={styles.filterRow}>
          <FormField label={t('admin.metrics.metricLabel')} className={styles.filterField}>
            <Select
              value={metric}
              options={TIMESERIES_METRICS.map((value) => ({
                value,
                label: t(TIMESERIES_METRIC_LABEL_KEYS[value] ?? value),
              }))}
              onChange={(event) => {
                const value = event.target.value;
                if (isMetric(value)) setMetric(value);
              }}
            />
          </FormField>
          {volumeCurrencies.length > 0 && volumeCurrency !== null ? (
            <FormField label={t('admin.metrics.currency')} className={styles.filterField}>
              <Select
                value={volumeCurrency}
                options={volumeCurrencies.map((value) => ({ value, label: value }))}
                onChange={(event) => {
                  setVolumeCurrencyChoice(event.target.value);
                }}
              />
            </FormField>
          ) : null}
          <FormField label={t('admin.metrics.periodLabel')} className={styles.filterField}>
            <Select
              value={metricPeriod}
              options={periodOptions}
              onChange={(event) => {
                const value = event.target.value;
                if (isPeriod(value)) setMetricPeriod(value);
              }}
            />
          </FormField>
        </div>

        {timeseries.isPending ? <Skeleton shape="block" height="180px" /> : null}
        {timeseries.isError ? (
          <p className={styles.error}>{adminErrorMessage(timeseries.error, t)}</p>
        ) : null}
        {timeseries.data ? (
          <SeriesChart
            points={chartPoints.map((point) => ({
              label: formatDate(point.bucket),
              value: point.value,
            }))}
            title={
              volumeCurrency === null
                ? t(TIMESERIES_METRIC_LABEL_KEYS[metric] ?? metric)
                : `${t(TIMESERIES_METRIC_LABEL_KEYS[metric] ?? metric)} (${volumeCurrency})`
            }
            // Hacim degerleri milyonlarca; kisa gosterim okunabilirligi kurtariyor.
            formatValue={metric === 'volume' ? formatCompact : formatCount}
            seriesIndex={metric === 'volume' ? 3 : 1}
            emptyLabel={t('admin.metrics.noSeries')}
          />
        ) : null}
      </Card>

      {/* ---------------- Son hareketler ---------------- */}
      <Card padding="md">
        <h2 className={styles.sectionTitle}>{t('admin.activity.title')}</h2>

        <ListStatus
          isPending={activity.isPending}
          isError={activity.isError}
          error={activity.error}
          isEmpty={activity.isSuccess && activityRows.length === 0}
          emptyIcon={<Activity size={28} aria-hidden="true" />}
          emptyTitle={t('admin.activity.empty')}
          onRetry={() => {
            void activity.refetch();
          }}
        />

        {activityRows.length > 0 ? (
          <>
            <div className={styles.auditList}>
              {activityRows.map((row) => {
                const eventKey = ACTIVITY_EVENT_LABEL_KEYS[row.eventType];
                return (
                  <div key={`${row.eventType}-${row.entityId}`} className={styles.auditRow}>
                    <div className={styles.badges}>
                      <span className={styles.auditAction}>
                        {eventKey === undefined ? row.eventType : t(eventKey)}
                      </span>
                      <Chip tone="neutral" size="sm">
                        {row.taskTypeCode}
                      </Chip>
                    </div>
                    <span className={styles.muted}>
                      {row.actorFullName || t('members.unknownUser')}
                      {row.actorOrganizationName === null ? '' : ` · ${row.actorOrganizationName}`}
                      {' · '}
                      {formatDateTime(row.occurredAt)}
                    </span>
                  </div>
                );
              })}
            </div>
            <LoadMore
              hasNextPage={activity.hasNextPage}
              isFetchingNextPage={activity.isFetchingNextPage}
              onLoadMore={() => {
                void activity.fetchNextPage();
              }}
            />
          </>
        ) : null}
      </Card>

      {/* ---------------- Duyurular ---------------- */}
      <div id="duyuru">
        <BroadcastComposer />
      </div>
    </div>
  );
}

/** Tutar + birim kodu. Birim gelmediyse (eski sunucu) yalniz sayi. */
const withUnit = (value: string, unit: string | null): string =>
  unit === null ? value : `${value} ${unit}`;
